import type { EfError } from '@form-factory/schema/contracts';
import { memo, useEffect, useState } from 'react';

import { parseEfError } from '@/hooks/useEfErrorHandler';

export type EfErrorAlertProps = {
  errors: EfError[] | unknown;
};

export const EfErrorAlert = memo(function EfErrorAlert({
  errors,
}: EfErrorAlertProps) {
  const [parsedErrors, setParsedErrors] = useState<EfError[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Errors can be async, which get parsed using parseEfError
  useEffect(() => {
    const handleErrorParsing = async () => {
      if (!errors) {
        return;
      }

      // If errors is already an array of EfError, use it directly
      if (Array.isArray(errors)) {
        setParsedErrors(errors);

        return;
      }

      // Otherwise, parse the error
      setIsLoading(true);
      try {
        const efErrors = await parseEfError(errors);
        setParsedErrors(efErrors);
      } catch (error) {
        console.error('Failed to parse error:', error);
        setParsedErrors([
          {
            responseCode: 500,
            message: 'Failed to parse error',
            path: '',
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    handleErrorParsing();
  }, [errors]);

  if (!parsedErrors.length) {
    return null;
  }

  return (
    <div className='mb-3 rounded-md bg-rose-900 p-3 text-left text-sm font-medium text-rose-50 animate-in fade-in zoom-in-90'>
      {isLoading ? 'Processing error...' : null}
      <ul className='list-disc space-y-1 pl-4'>
        {parsedErrors.map((error, index) => (
          <li key={error.path || index}>{error.message}</li>
        ))}
      </ul>
    </div>
  );
});
